import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { businessService } from '@/services/business.service';
import { useForm } from 'react-hook-form';
import {
  ChevronDown,
  ChevronRight,
  Plug2,
  RefreshCw,
  Save,
  ArrowLeft,
} from 'lucide-react';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import { LTI_INTEGRATION_PERMISSION } from '@/constants/constant-permissions';
import {
  useCreateLtiPlatform,
  useLtiPlatform,
  useUpdateLtiPlatform,
} from '@/hooks/useLti';
import { RegisterLtiPlatformRequest } from '@/types/lti.types';
import { ADMIN_LTI_INTEGRATIONS } from '@/constants/constant-routes';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';

// Form shape: array fields are edited as newline/comma separated text.
interface LtiPlatformForm {
  issuer: string;
  client_id: string;
  authorization_endpoint: string;
  token_endpoint: string;
  jwks_url: string;
  audience?: string;
  deployment_ids_text?: string;
  allowed_iframe_origins_text?: string;
  ags_enabled: boolean;
  deep_linking_enabled: boolean;
  nrps_enabled: boolean;
}

/**
 * A URL check that accepts http://localhost:8080 — the address used against
 * any local LMS — while still rejecting malformed input.
 */
const isHttpUrl = (value?: string | null): boolean => {
  if (!value) return true; // emptiness is the required rule's job
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

// No resolver: the shared Input/Textarea apply their clean-text rule on top of these
const httpUrlRules = (label: string) => ({
  required: `${label} is required`,
  validate: (value: string) => isHttpUrl(value) || 'Must be a valid http(s) URL',
});

const defaults: LtiPlatformForm = {
  issuer: '',
  client_id: '',
  authorization_endpoint: '',
  token_endpoint: '',
  jwks_url: '',
  audience: '',
  deployment_ids_text: '',
  allowed_iframe_origins_text: '',
  ags_enabled: true,
  deep_linking_enabled: true,
  nrps_enabled: false,
};

/** Split a comma/newline separated string into a trimmed, non-empty array. */
function toList(text?: string): string[] {
  if (!text) return [];
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function AddLtiPlatform() {
  usePermissionRouteAccess([
    LTI_INTEGRATION_PERMISSION.ADMIN_LTI_INTEGRATION_WRITE,
  ]);
  const navigate = useNavigate();

  const { data: businessData } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
    staleTime: 5 * 60 * 1000,
  });
  const companyName = businessData?.data?.company_name?.trim() || businessData?.company_name?.trim() || 'Skillzen';
  const { id } = useParams();
  const isEditMode = !!id;

  const { data: existing, isLoading: loadingExisting } = useLtiPlatform(id);
  const createMutation = useCreateLtiPlatform();
  const updateMutation = useUpdateLtiPlatform();

  const [sections, setSections] = useState({
    identity: true,
    endpoints: true,
    advantage: true,
    security: isEditMode,
  });
  const toggle = (key: keyof typeof sections) =>
    setSections((s) => ({ ...s, [key]: !s[key] }));

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<LtiPlatformForm>({
    // Validate while typing so errors (e.g. clean-text) show under the field, not only after Save
    mode: 'onChange',
    defaultValues: defaults,
  });

  useEffect(() => {
    if (existing) {
      reset({
        issuer: existing.issuer,
        client_id: existing.client_id,
        authorization_endpoint: existing.authorization_endpoint,
        token_endpoint: existing.token_endpoint,
        jwks_url: existing.jwks_url,
        audience: existing.audience || '',
        // Blank is safe: edit mode hides this field and omits deployment_ids
        // from the payload below, and updatePlatform ignores it regardless.
        deployment_ids_text: '',
        allowed_iframe_origins_text: (existing.allowed_iframe_origins || []).join(
          '\n',
        ),
        ags_enabled: existing.ags_enabled,
        deep_linking_enabled: existing.deep_linking_enabled,
        nrps_enabled: existing.nrps_enabled,
      });
    }
  }, [existing, reset]);

  const onSubmit = (form: LtiPlatformForm) => {
    const payload: RegisterLtiPlatformRequest = {
      issuer: form.issuer.trim(),
      client_id: form.client_id.trim(),
      authorization_endpoint: form.authorization_endpoint.trim(),
      token_endpoint: form.token_endpoint.trim(),
      jwks_url: form.jwks_url.trim(),
      audience: form.audience?.trim() || undefined,
      // Only on create. updatePlatform ignores this field — deployments are
      // made at registration, and a new one registers itself on first launch.
      ...(isEditMode ? {} : { deployment_ids: toList(form.deployment_ids_text) }),
      allowed_iframe_origins: toList(form.allowed_iframe_origins_text),
      ags_enabled: form.ags_enabled,
      deep_linking_enabled: form.deep_linking_enabled,
      nrps_enabled: form.nrps_enabled,
    };

    if (isEditMode) {
      updateMutation.mutate(
        { id: id!, data: payload },
        { onSuccess: () => navigate(ADMIN_LTI_INTEGRATIONS) },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => navigate(ADMIN_LTI_INTEGRATIONS),
      });
    }
  };

  const saving =
    isSubmitting || createMutation.isPending || updateMutation.isPending;

  const SectionHeader = ({
    keyName,
    title,
    subtitle,
  }: {
    keyName: keyof typeof sections;
    title: string;
    subtitle?: string;
  }) => (
    <button
      type="button"
      onClick={() => toggle(keyName)}
      className="w-full flex items-center justify-between text-left"
    >
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      {sections[keyName] ? (
        <ChevronDown className="w-5 h-5 text-gray-400" />
      ) : (
        <ChevronRight className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  if (isEditMode && loadingExisting) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 gap-2">
        <RefreshCw className="w-5 h-5 animate-spin" /> Loading platform...
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(ADMIN_LTI_INTEGRATIONS)}
          className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Plug2 className="w-6 h-6 text-brand-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit LMS Platform' : 'Register LMS Platform'}
          </h1>
        </div>
      </div>

      {/* Platform identity */}
      <div className="card space-y-4">
        <SectionHeader
          keyName="identity"
          title="Platform identity"
          subtitle={`How the LMS identifies itself and the ${companyName} tool registration.`}
        />
        {sections.identity && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input<LtiPlatformForm>
              name="issuer"
              control={control}
              label="Issuer"
              required
              type="text"
              disableRTL
              placeholder="https://moodle.example.com"
              helperText="The platform 'iss' value. In edit mode this should not change."
              rules={{ required: 'Issuer is required' }}
            />
            <Input<LtiPlatformForm>
              name="client_id"
              control={control}
              label="Client ID"
              required
              type="text"
              disableRTL
              placeholder={`The client_id the LMS assigned to ${companyName}`}
              rules={{ required: 'Client ID is required' }}
            />
          </div>
        )}
      </div>

      {/* LMS endpoints */}
      <div className="card space-y-4">
        <SectionHeader
          keyName="endpoints"
          title="LMS endpoints"
          subtitle="OIDC / OAuth 2.0 endpoints, taken from the LMS tool registration."
        />
        {sections.endpoints && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input<LtiPlatformForm>
              name="authorization_endpoint"
              control={control}
              label="Authorization endpoint"
              required
              type="url"
              placeholder="https://moodle.example.com/mod/lti/auth.php"
              rules={httpUrlRules('Authorization endpoint')}
            />
            <Input<LtiPlatformForm>
              name="token_endpoint"
              control={control}
              label="Token endpoint"
              required
              type="url"
              placeholder="https://moodle.example.com/mod/lti/token.php"
              rules={httpUrlRules('Token endpoint')}
            />
            <Input<LtiPlatformForm>
              name="jwks_url"
              control={control}
              label="JWKS URL"
              required
              type="url"
              placeholder="https://moodle.example.com/mod/lti/certs.php"
              helperText="Where we fetch the LMS public keys to verify launches."
              rules={httpUrlRules('JWKS URL')}
            />
            <Input<LtiPlatformForm>
              name="audience"
              control={control}
              label="Audience (optional)"
              type="text"
              disableRTL
              placeholder="Defaults to the token endpoint"
            />
          </div>
        )}
      </div>

      {/* LTI Advantage */}
      <div className="card space-y-4">
        <SectionHeader
          keyName="advantage"
          title="LTI Advantage services"
          subtitle="Enable the service suite this platform supports."
        />
        {sections.advantage && (
          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input type="checkbox" {...register('ags_enabled')} className="rounded border-gray-300" />
              <span className="text-sm text-gray-700">
                <strong>AGS</strong> — Assignment &amp; Grade Services (grade
                passback)
              </span>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" {...register('deep_linking_enabled')} className="rounded border-gray-300" />
              <span className="text-sm text-gray-700">
                <strong>Deep Linking</strong> — let instructors pick {companyName} content from inside the LMS
              </span>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" {...register('nrps_enabled')} className="rounded border-gray-300" />
              <span className="text-sm text-gray-700">
                <strong>NRPS</strong> — Names &amp; Role Provisioning Services
                (roster sync)
              </span>
            </label>
            <div className="pt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Deployment IDs
              </label>
              {isEditMode ? (
                <p className="text-gray-500 text-xs">
                  Deployments are registered when the platform is first created, and any
                  new one registers itself on its first launch. There is nothing to change
                  here.
                </p>
              ) : (
                <Textarea<LtiPlatformForm>
                  name="deployment_ids_text"
                  control={control}
                  className="font-mono"
                  rows={2}
                  disableRTL
                  placeholder="One per line, or comma-separated (e.g. 1)"
                  helperText="Issued by the LMS when the tool is deployed. New deployments are also auto-registered on first launch."
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Security */}
      <div className="card space-y-4">
        <SectionHeader
          keyName="security"
          title="Security"
          subtitle={`Restrict which origins may embed ${companyName} launches.`}
        />
        {sections.security && (
          <div>
            {/* URL list: symbols are expected, so each line must be an http(s) URL instead */}
            <Textarea<LtiPlatformForm>
              name="allowed_iframe_origins_text"
              control={control}
              label="Allowed iframe origins"
              className="font-mono"
              rows={2}
              allowSpecialChars
              disableRTL
              rules={{
                validate: (value: string) =>
                  toList(value).every((origin) => isHttpUrl(origin)) || 'Each line must be a valid http(s) URL',
              }}
              placeholder="One per line (e.g. https://moodle.example.com)"
              helperText={`The Issuer's site is allowed to embed ${companyName} automatically. Add an origin here only when learners see the LMS on a different address (e.g. Canvas: https://school.instructure.com). Updates apply within 5 minutes.`}
            />
          </div>
        )}
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 right-0 left-0 md:left-64 bg-white border-t border-gray-200 p-4 z-10">
        <div className="flex items-center justify-end gap-3 px-2">
          <button
            type="button"
            onClick={() => navigate(ADMIN_LTI_INTEGRATIONS)}
            className="btn btn-outline"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditMode ? 'Update' : 'Register'} Platform
          </button>
        </div>
      </div>
    </form>
  );
}
